import { useCallback } from "react";
import {
	DragDropContext,
	Draggable,
	Droppable,
	type DropResult,
} from "react-beautiful-dnd";
import { FiPlus } from "react-icons/fi";

import { Button } from "newui/components/Button";
import type { LocalColumn, ScheduleColumnType } from "../schedule-of-rates.types";
import { ScheduleColumnConfigItem } from "./ScheduleColumnConfigItem";

export interface ScheduleColumnConfigPanelProps {
	columns: LocalColumn[];
	onAddColumn: (type: ScheduleColumnType) => void;
	onLabelChange: (localId: string, label: string) => void;
	onMoveColumn: (localId: string, direction: "up" | "down") => void;
	onRemoveColumn: (localId: string) => void;
	onReorderColumns: (sourceIndex: number, destIndex: number) => void;
	onTypeChange: (localId: string, type: ScheduleColumnType) => void;
}

const ScheduleColumnConfigPanel: React.FC<ScheduleColumnConfigPanelProps> = ({
	columns,
	onAddColumn,
	onLabelChange,
	onMoveColumn,
	onRemoveColumn,
	onReorderColumns,
	onTypeChange,
}) => {
	const handleDragEnd = useCallback(
		(result: DropResult) => {
			if (!result.destination) {
				return;
			}
			onReorderColumns(result.source.index, result.destination.index);
		},
		[onReorderColumns]
	);

	return (
		<div>
			<p className="mb-2 font-roboto text-sm font-semibold text-gray-900">
				Columns
			</p>

			{/* Column header labels */}
			<div className="mb-1 flex items-center gap-2 px-2">
				<div className="w-[14px]" />
				<p className="flex-1 text-xs font-medium text-gray-500">
					Header Text
				</p>
				<p className="w-[200px] text-xs font-medium text-gray-500">
					Column Type
				</p>
				<div className="w-[24px]" />
				<div className="w-[14px]" />
			</div>

			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="schedule-column-editor" direction="vertical">
					{(provided) => (
						<div
							ref={provided.innerRef}
							{...provided.droppableProps}
							className="space-y-1"
							data-testid="schedule-column-editor"
						>
							{columns.map((col, idx) => (
								<Draggable
									key={col.localId}
									draggableId={col.localId}
									index={idx}
								>
									{(dragProvided) => (
										<div
											ref={dragProvided.innerRef}
											{...dragProvided.draggableProps}
										>
											<ScheduleColumnConfigItem
												column={col}
												dragHandleProps={dragProvided.dragHandleProps}
												index={idx}
												isFirst={idx === 0}
												isLast={idx === columns.length - 1}
												isOnlyColumn={columns.length <= 1}
												onLabelChange={onLabelChange}
												onMoveColumn={onMoveColumn}
												onRemoveColumn={onRemoveColumn}
												onTypeChange={onTypeChange}
											/>
										</div>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>

			<div className="mt-3">
				<Button
					btntype="plain-border"
					data-testid="schedule-add-column-btn"
					onClick={() => onAddColumn("text")}
					size="sm"
					type="button"
				>
					<FiPlus className="mr-1" />
					Add Column
				</Button>
			</div>
		</div>
	);
};

export { ScheduleColumnConfigPanel };
